import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "loading", "pressed", "icon", "onClick", "onFocus", "onBlur", "variant", "size", "autoFocus"];
import styled from 'foundations-theming/utils';
import { isValidElement } from 'react';
import { mergeProps } from 'react-aria';
import { getSizeTokens, getVariantTokens } from './Button.helpers.ts';
import { useVariantCssObject, useSizeCssObject } from './Button.hooks';
import { useTrellisFocusRing } from '../../hooks/useTrellisFocusRing';
import { useTrellisButton } from '../../hooks/useTrellisButton';
import { Flex } from '../Flex';
import { filterProps } from '../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-7z9te4-0"
})(({
  $variantCssStyles,
  $sizeCssStyles,
  $focusRingStyles,
  $spaceCssStyles
}) => {
  return Object.assign({
    all: 'unset',
    cursor: 'pointer'
  }, $variantCssStyles, $sizeCssStyles, $focusRingStyles, $spaceCssStyles);
});
/*
 * We needed to explicitly type this function because Styled Components was having an issue with the union type of the $typography prop.
 * See https://hubspot.slack.com/archives/C4ABAB8CR/p1722362981866609
 */
const StyledButtonTextContainer = styled.span.withConfig({
  displayName: "Button__StyledButtonTextContainer",
  componentId: "sc-7z9te4-1"
})(({
  $typography,
  theme: _theme
}) => {
  const theme = _theme;
  return Object.assign({
    boxSizing: 'border-box',
    fontStyle: 'unset',
    textTransform: 'unset',
    backgroundColor: 'unset',
    margin: theme.space['0'],
    padding: theme.space['0']
  }, theme.typography[$typography], {
    color: 'inherit' // The color is set on the button element via the variant tokens, so we can just inherit it here
  });
});
export function Button(_ref) {
  let {
      children,
      disabled = false,
      loading = false,
      pressed,
      icon: Icon,
      onClick = () => {},
      onFocus = () => {},
      onBlur = () => {},
      variant = 'primary',
      size = 'medium',
      autoFocus = false
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handleClick = () => {
    if (disabled || loading) return;
    onClick();
  };
  const handleFocus = () => {
    onFocus();
  };
  const handleBlur = () => {
    onBlur();
  };
  const {
    buttonProps,
    ref: buttonRef
  } = useTrellisButton(Object.assign({}, props, {
    'aria-pressed': pressed,
    autoFocus,
    onClick: handleClick,
    onFocus: handleFocus,
    onBlur: handleBlur
  }));
  const variantTokens = getVariantTokens(variant);
  const variantCssStyles = useVariantCssObject(variantTokens);
  const {
    focusRingStyles,
    focusProps
  } = useTrellisFocusRing({
    autoFocus
  });
  const sizeTokens = getSizeTokens(size, variant);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const mergedProps = mergeProps(buttonProps, focusProps);
  const {
    domProps,
    tokenProps
  } = filterProps(props);
  const spaceCssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(StyledButton, Object.assign({}, mergedProps, domProps, {
    ref: buttonRef,
    "aria-disabled": disabled || loading ? true : undefined,
    $variantCssStyles: variantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $focusRingStyles: focusRingStyles,
    $spaceCssStyles: spaceCssStyles,
    children: /*#__PURE__*/_jsx(StyledButtonTextContainer, {
      $typography: sizeTokens.typography,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        columnGap: sizeTokens.gap,
        as: "span",
        children: [Icon && /*#__PURE__*/isValidElement(Icon) ? Icon : Icon && typeof Icon === 'function' && /*#__PURE__*/_jsx(Icon, {}), children]
      })
    })
  }));
}